import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@pkg/evm'
import { createSolanaActionSource } from '@pkg/solana'
import {
  ApiPassphraseRequiredException,
  ApiSecretRequiredException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { networkEvmChainIds, solanaNetwork } from '../constants'
import { createClient } from '../createClient'
import { fetchUnifiedRecordsInWindows } from '../fetchUnifiedRecordsInWindows'
import { runWithAccountModeFallback } from '../runWithAccountModeFallback'
import { responseSchema, responseSchemaV3 } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  apiPassphrase,
  config,
  from,
  createHeaders,
  proxy,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }
  if (!apiPassphrase) {
    throw new ApiPassphraseRequiredException()
  }
  if (!from || !to) {
    throw new Error('Both from and to parameters are required')
  }

  const client = createClient({
    apiKey,
    apiSecret,
    apiPassphrase,
    createHeaders,
    proxy,
  })

  // Classic (v2) を試し、Unified Account の場合だけ v3 へ切り替える。
  // v3 は 30 日を超える範囲を拒否するため、v3 側だけ窓を分割して取得する。
  const records = await runWithAccountModeFallback({
    classic: async () =>
      (
        await client.run({
          method: 'GET',
          path: '/api/v2/spot/wallet/deposit-records',
          query: {
            startTime: from.toString(),
            endTime: to.toString(),
            limit: config.limit.toString(),
          },
          responseSchema,
        })
      ).data,
    unified: async () =>
      await fetchUnifiedRecordsInWindows({
        from,
        to,
        limit: config.limit,
        order: config.order,
        fetchWindow: async ({ startTime, endTime }) =>
          (
            await client.run({
              method: 'GET',
              path: '/api/v3/account/deposit-records',
              query: {
                startTime: startTime.toString(),
                endTime: endTime.toString(),
                limit: config.limit.toString(),
              },
              responseSchema: responseSchemaV3,
            })
          ).data,
      }),
  })

  // 昇順 sort by cTime
  const sortedDeposits = [...records].sort(
    (a, b) => Number.parseInt(a.cTime) - Number.parseInt(b.cTime),
  )

  // データ処理
  let stopped = false
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedDeposits) {
    // Skip failed deposits
    if (item.status === 'fail') {
      continue
    }

    // Stop processing if deposit is pending
    if (item.status === 'pending') {
      stopped = true
      break
    }

    const cTime = Number.parseInt(item.cTime)

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? cTime : Math.min(fetchedFrom, cTime)
    fetchedTo = fetchedTo === undefined ? cTime : Math.max(fetchedTo, cTime)

    const chain = item.chain.includes('(')
      ? item.chain.split('(')[1].split(')')[0]
      : item.chain

    // Create EVM source if applicable
    if (chain && item.tradeId) {
      const evmChainId = networkEvmChainIds[chain]
      if (evmChainId && isHex(item.tradeId)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: item.tradeId,
          }),
        )
      }
    }

    // Create Solana source if applicable
    if (chain === solanaNetwork && item.tradeId) {
      sources.push(createSolanaActionSource({ signature: item.tradeId }))
    }
  }

  return {
    actions,
    count: records.length,
    from: fetchedFrom,
    sources,
    stopped,
    to: fetchedTo,
  }
}
